// React port of Angular product-selection.component.ts. Cascading
// inline wizard: product → style → customise → add-to-order. Shares
// in-progress configuration with ProductPreviewRenderer (and the
// catalogue modal) via the singleton productSelectionStore. The cart
// itself lives on a page variable (block.cartBinding); the unsaved
// configuration draft lives in the singleton.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRuntime } from '@/pages/workbench/smartpage-preview/RuntimeContext'
import type { ProductSelectionBlock } from '@/pages/designers/page/types'
import {
  apiGet, erpErrorMessage, extractItems, getProductDetail, getStyleMeasurements,
  getStylePreferences, listProducts, listStylesForProduct,
  type ErpFieldDef, type ErpProduct, type ErpStyle,
} from '../erpService'
import { livePreviewList, type ListFilter } from '../api'
import { outputVarName, readBinding, resolveArgsAsRecord, resolveRef } from '../bindingResolver'
import { resolveEndpointUrl } from './endpoints'
import { productSelectionStore, useProductSelectionState } from '../productSelectionState'
import { injectEcomStyles } from './styles'
import ProductPreviewRenderer from './ProductPreviewRenderer'

type Step = 'product' | 'style' | 'customize'
type SelectorMode = 'wizard' | 'catalogue' | 'both'

function fetchProductsForBlock(
  block: ProductSelectionBlock,
  ctx: { vars: Record<string, unknown>; inputs: ReturnType<typeof useRuntime>['page']['inputs'] },
  project: string,
): Promise<ErpProduct[]> {
  if (block.source.kind === 'transaction') {
    const objName = block.source.obj_name
    if (!project || !objName) return Promise.resolve([])
    const normalize = (col: string) => (col || '').trim().replace(/\s+/g, '_').toLowerCase()
    const filters: ListFilter[] = (block.source.filters || [])
      .filter((f) => !!f.column && !!f.op)
      .map((f) => {
        if (f.op === 'between') {
          return { column: normalize(f.column), op: f.op,
            from: resolveRef(f.from, ctx), to: resolveRef(f.to, ctx) }
        }
        return { column: normalize(f.column), op: f.op,
          value: resolveRef(f.value, ctx) }
      })
    return livePreviewList(project, objName, 50, 0, undefined, undefined, filters)
      .then((res) => (res?.rows || []) as unknown as ErpProduct[])
  }
  // API source: built-in 'products' uses the typed listProducts; custom
  // URL uses generic apiGet so authors can plug in any endpoint that
  // returns { items: [...] } (or any of the extractItems envelopes).
  if (block.source.endpointId === 'products') {
    const args = resolveArgsAsRecord(block.source.args, ctx) as Record<string, unknown>
    return listProducts({
      page: 1, pageSize: 24, sort: 'popular',
      ...(typeof args.q === 'string' ? { q: args.q } : {}),
      ...(typeof args.category === 'string' ? { category: args.category } : {}),
    }).then((res) => Array.isArray(res?.items) ? res.items : [])
  }
  const url = resolveEndpointUrl(block.source)
  if (!url) return Promise.resolve([])
  return apiGet(url, resolveArgsAsRecord(block.source.args, ctx))
    .then((data) => extractItems(data) as unknown as ErpProduct[])
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
}

function effectiveMode(block: ProductSelectionBlock): SelectorMode {
  if (block.selectorMode) return block.selectorMode
  switch (block.catalogueMode) {
    case 'modal-only': return 'catalogue'
    case 'both': return 'both'
    case 'inline-only':
    default: return 'wizard'
  }
}

function hasImagePills(f: ErpFieldDef): boolean {
  return !!(f.options || []).some((o) => !!o.imageUrl)
}

function optionLabel(f: ErpFieldDef, value: unknown): string {
  const opt = (f.options || []).find((o) => o.value === value)
  return opt ? opt.label : ''
}

export default function ProductSelectionRenderer({ block }: { block: ProductSelectionBlock }) {
  injectEcomStyles()
  const runtime = useRuntime()
  const sel = useProductSelectionState()

  const ctx = useMemo(
    () => ({ vars: runtime.vars, inputs: runtime.page.inputs || [] }),
    [runtime.vars, runtime.page.inputs],
  )

  const [step, setStep] = useState<Step>(block.initialStep || 'product')
  const [products, setProducts] = useState<ErpProduct[]>([])
  const [styles, setStyles] = useState<ErpStyle[]>([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [loadingStyles, setLoadingStyles] = useState(false)
  const [loadingSchemas, setLoadingSchemas] = useState(false)
  const [productsError, setProductsError] = useState('')

  // Cart binding: same var is read AND written. outputVarName extracts
  // the destination page-var name from the page_input source.
  const cartVarName = outputVarName(block.cartBinding)
  // Gate binding: optional read — when set + falsy, render the gate UI.
  const gateValue = readBinding(block.gateBinding, ctx)
  const gated = block.gateBinding ? !gateValue : false
  // Modal-open binding: optional two-way boolean for programmatic open/close.
  const openInModalName = outputVarName(block.openInModalBinding)

  const mode = effectiveMode(block)
  const currency = sel.selectedProduct?.currency || 'INR'

  // ── Fetch products when ungated ────────────────────────────
  const fetchedRef = useRef(false)
  useEffect(() => {
    if (gated) return
    if (fetchedRef.current) return
    fetchedRef.current = true
    setLoadingProducts(true)
    setProductsError('')
    fetchProductsForBlock(block, ctx, runtime.project)
      .then((items) => {
        setProducts(items)
        setLoadingProducts(false)
      })
      .catch((err) => {
        setProductsError(erpErrorMessage(err, 'Failed to load products.'))
        setLoadingProducts(false)
        fetchedRef.current = false
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gated, block.id])

  // ── React to "edit cart line" requested by OrderSummary ───
  const editLoadingRef = useRef<string | null>(null)
  useEffect(() => {
    const id = sel.editingCartItemId
    if (!id) {
      editLoadingRef.current = null
      return
    }
    if (editLoadingRef.current === id) return
    editLoadingRef.current = id
    startEditLoad(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.editingCartItemId])

  // ── openInModalBinding two-way bridge ─────────────────────
  useEffect(() => {
    if (!openInModalName) return
    const v = runtime.vars[openInModalName]
    if (v && !sel.modalOpen) productSelectionStore.openModal()
    if (!v && sel.modalOpen) productSelectionStore.closeModal()
  }, [openInModalName, runtime.vars, sel.modalOpen])

  function pickProduct(p: ErpProduct) {
    productSelectionStore.setSelectedProduct(p)
    productSelectionStore.setSelectedStyle(null)
    productSelectionStore.setUnitPrice(p.basePrice)
    setStep('style')
    setLoadingStyles(true)
    setStyles([])
    listStylesForProduct(p.id)
      .then((items) => {
        setStyles(items)
        setLoadingStyles(false)
        if (items.length === 1) pickStyle(items[0])
      })
      .catch(() => setLoadingStyles(false))
  }

  function pickStyle(s: ErpStyle) {
    // Mirror Angular pickStyle order exactly: set style, set unit
    // price (base + delta), clear customise inputs. The previous
    // implementation called resetStyle() here which nulled
    // selectedStyle and overwrote the unitPrice back to basePrice.
    const p = productSelectionStore.get().selectedProduct
    productSelectionStore.setSelectedStyle(s)
    productSelectionStore.setUnitPrice((p?.basePrice || 0) + (s.priceDelta || 0))
    productSelectionStore.clearCustomiseInputs()
    setStep('customize')
    setLoadingSchemas(true)

    let pref = false, meas = false
    const done = () => { if (pref && meas) setLoadingSchemas(false) }

    getStylePreferences(s.id)
      .then((schema) => { productSelectionStore.setPrefSchema(schema); pref = true; done() })
      .catch(() => { pref = true; done() })
    getStyleMeasurements(s.id)
      .then((schema) => { productSelectionStore.setMeasSchema(schema); meas = true; done() })
      .catch(() => { meas = true; done() })
  }

  function startEditLoad(cartItemId: string) {
    const cartRaw = cartVarName ? runtime.vars[cartVarName] : undefined
    if (!Array.isArray(cartRaw)) { productSelectionStore.cancelEdit(); return }
    const item = (cartRaw as Array<Record<string, unknown>>).find((c) => c?.cartItemId === cartItemId)
    if (!item || !item.productId || !item.styleId) {
      productSelectionStore.cancelEdit()
      return
    }
    const productId = String(item.productId)
    const styleId = String(item.styleId)
    setStep('customize')
    setLoadingSchemas(true)

    getProductDetail(productId)
      .then((p) => {
        if (editLoadingRef.current !== cartItemId) return
        productSelectionStore.setSelectedProduct(p)
      })
      .catch(() => { /* keep going */ })

    listStylesForProduct(productId)
      .then((items) => {
        if (editLoadingRef.current !== cartItemId) return
        const found = items.find((s) => s.id === styleId) ?? null
        productSelectionStore.setSelectedStyle(found)
        setStyles(items)
      })
      .catch(() => { /* schemas can still come */ })

    let pref = false, meas = false
    const done = () => {
      if (pref && meas && editLoadingRef.current === cartItemId) setLoadingSchemas(false)
    }
    getStylePreferences(styleId)
      .then((schema) => {
        if (editLoadingRef.current === cartItemId) productSelectionStore.setPrefSchema(schema)
        pref = true; done()
      })
      .catch(() => { pref = true; done() })
    getStyleMeasurements(styleId)
      .then((schema) => {
        if (editLoadingRef.current === cartItemId) productSelectionStore.setMeasSchema(schema)
        meas = true; done()
      })
      .catch(() => { meas = true; done() })
  }

  function goTo(s: Step) {
    if (s === 'product') productSelectionStore.resetConfiguration()
    else if (s === 'style') productSelectionStore.resetStyle()
    setStep(s)
  }

  function addToOrder() {
    if (!productSelectionStore.validate()) return
    if (!cartVarName) {
      // No cart binding configured — nothing to write to. Reset and bail.
      productSelectionStore.resetConfiguration()
      setStep('product')
      return
    }
    const currentCart = runtime.vars[cartVarName]
    const result = productSelectionStore.pushToCart(currentCart)
    if (!result) return
    runtime.setVar(cartVarName, result.cart)
    productSelectionStore.resetConfiguration()
    setStep('product')
  }

  function cancelEdit() {
    productSelectionStore.cancelEdit()
    productSelectionStore.resetConfiguration()
    setStep('product')
  }

  function openCatalogue() {
    productSelectionStore.openModal()
    if (openInModalName) runtime.setVar(openInModalName, true)
  }

  function closeCatalogue() {
    productSelectionStore.closeModal()
    if (openInModalName) runtime.setVar(openInModalName, false)
  }

  // ── Validation banner ──
  const validationBanner = useMemo(() => {
    const prefCount = Object.keys(sel.prefErrors).length
    const measCount = Object.keys(sel.measErrors).length
    if (prefCount === 0 && measCount === 0) return ''
    const parts: string[] = []
    if (prefCount > 0) parts.push(`${prefCount} preference${prefCount === 1 ? '' : 's'}`)
    if (measCount > 0) parts.push(`${measCount} measurement${measCount === 1 ? '' : 's'}`)
    return `Please complete the highlighted ${parts.join(' and ')} before adding to the order.`
  }, [sel.prefErrors, sel.measErrors])

  const hasEmbeddedPreview =
    block.previewDomain && block.previewDomain !== 'none' && !!sel.selectedStyle

  // ── Render ─────────────────────────────────────────────
  if (gated) {
    const gateVar = outputVarName(block.gateBinding)
    return (
      <section className="ps" data-ecom-block="product_selection">
        <div className="ps__gate">
          <div className="ps__gate-icon">🔒</div>
          <div className="ps__gate-title">{gateVar ? `Set "${gateVar}" first` : 'Selection gated'}</div>
          <div className="ps__gate-body">Use the picker block above to provide the required selection.</div>
        </div>
      </section>
    )
  }

  if (mode === 'catalogue') {
    return (
      <section className="ps" data-ecom-block="product_selection">
        <div className="ps__cataloguecta">
          <div className="ps__cataloguecta-icon" aria-hidden="true">🛍️</div>
          <div className="ps__cataloguecta-title">{block.catalogueTitle || 'Catalogue'}</div>
          <div className="ps__cataloguecta-body">
            Browse products, pick styles, and configure each item in the catalogue.
          </div>
          <button type="button" className="ps__btn ps__btn--primary" onClick={openCatalogue}>
            {block.catalogueButtonLabel || 'Browse catalogue'} ⤢
          </button>
        </div>
        {sel.modalOpen && <CatalogueModal block={block} onClose={closeCatalogue} />}
      </section>
    )
  }

  return (
    <section className="ps" data-ecom-block="product_selection">
      <header className="ps__head">
        <ol className="ps__steps">
          <li className={`ps__step${step === 'product' ? ' is-active' : ''}${sel.selectedProduct ? ' is-done' : ''}`}>
            <span className="ps__num">1</span><span className="ps__lbl">Product</span>
          </li>
          <li className={`ps__step${step === 'style' ? ' is-active' : ''}${sel.selectedStyle ? ' is-done' : ''}`}>
            <span className="ps__num">2</span><span className="ps__lbl">Style</span>
          </li>
          <li className={`ps__step${step === 'customize' ? ' is-active' : ''}`}>
            <span className="ps__num">3</span><span className="ps__lbl">Customise</span>
          </li>
        </ol>
        {mode === 'both' && (
          <button type="button" className="ps__catalogue" onClick={openCatalogue}>
            {block.catalogueButtonLabel || 'Browse catalogue'} ⤢
          </button>
        )}
      </header>

      {(sel.selectedProduct || sel.selectedStyle) && (
        <nav className="ps__crumbs">
          {sel.selectedProduct && (
            <button className="ps__crumb" type="button"
                    onClick={() => goTo('product')} disabled={step === 'product'}>
              {sel.selectedProduct.name}
            </button>
          )}
          {sel.selectedStyle && (
            <>
              <span className="ps__crumb-sep">›</span>
              <button className="ps__crumb" type="button"
                      onClick={() => goTo('style')} disabled={step === 'style'}>
                {sel.selectedStyle.name}
              </button>
            </>
          )}
        </nav>
      )}

      {step === 'product' && (
        loadingProducts ? <div className="ps__hint">Loading products…</div>
        : productsError ? <div className="ps__error">{productsError}</div>
        : (
          <div
            className={`ps__grid${block.layout === 'list' ? ' ps__grid--list' : ''}`}
            style={{
              gridTemplateColumns:
                block.layout === 'list' ? '1fr' : `repeat(${block.columns || 3}, 1fr)`,
            }}
          >
            {products.map((p) => (
              <button key={p.id} type="button" className="ps__card" onClick={() => pickProduct(p)}>
                <div className="ps__cardimg" style={{ backgroundImage: `url(${p.imageUrl})` }} />
                <div className="ps__cardbody">
                  <div className="ps__cardname">{p.name}</div>
                  <div className="ps__cardprice">{formatCurrency(p.basePrice, p.currency)}</div>
                  {p.metadata['fabric'] && (
                    <div className="ps__cardmeta">{p.metadata['fabric']} · {p.metadata['season']}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {step === 'style' && (
        loadingStyles ? <div className="ps__hint">Loading styles…</div>
        : styles.length === 0 ? <div className="ps__hint">No styles available for this product.</div>
        : (
          <div
            className="ps__grid"
            style={{ gridTemplateColumns: `repeat(${Math.min(styles.length, 3)}, 1fr)` }}
          >
            {styles.map((s) => (
              <button key={s.id} type="button" className="ps__card ps__card--style" onClick={() => pickStyle(s)}>
                {s.imageUrl && <div className="ps__cardimg" style={{ backgroundImage: `url(${s.imageUrl})` }} />}
                <div className="ps__cardbody">
                  <div className="ps__cardname">{s.name}</div>
                  {s.priceDelta !== 0 && (
                    <div className={`ps__delta${s.priceDelta > 0 ? ' is-up' : ''}`}>
                      {s.priceDelta > 0 ? '+' : ''}{formatCurrency(s.priceDelta, currency)}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )
      )}

      {step === 'customize' && (
        loadingSchemas ? <div className="ps__hint">Loading customisation options…</div>
        : (
          <div className={`ps__customize-wrap${hasEmbeddedPreview ? ' ps__customize-wrap--split' : ''}`}>
            <div className="ps__customize">
              {sel.prefSchema && (
                <section className="ps__section">
                  <h4 className="ps__sectitle">Preferences</h4>
                  <div className="ps__fields">
                    {sel.prefSchema.fields.map((f) => (
                      <PrefField
                        key={f.key}
                        field={f}
                        value={sel.prefValues[f.key]}
                        error={sel.prefErrors[f.key]}
                        blockId={block.id}
                      />
                    ))}
                  </div>
                </section>
              )}

              {sel.measSchema && (
                <section className="ps__section">
                  <h4 className="ps__sectitle">
                    Measurements <span className="ps__unit">({sel.measSchema.unit})</span>
                  </h4>
                  {sel.measSchema.groups.map((g) => (
                    <div key={g.key} className="ps__group">
                      <div className="ps__grouplabel">{g.label}</div>
                      <div className="ps__measgrid">
                        {g.fields.map((f) => (
                          <MeasField
                            key={f.key}
                            field={f}
                            value={sel.measValues[f.key]}
                            error={sel.measErrors[f.key]}
                            unit={sel.measSchema!.unit}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </section>
              )}

              <section className="ps__section ps__section--qty">
                <div className="ps__qtyrow">
                  <label className="ps__fieldlabel">Quantity</label>
                  <div className="ps__qty">
                    <button type="button" className="ps__qbtn"
                            onClick={() => productSelectionStore.bumpQty(-1)}
                            disabled={sel.qty <= 1} aria-label="Decrease">−</button>
                    <span className="ps__qval">{sel.qty}</span>
                    <button type="button" className="ps__qbtn"
                            onClick={() => productSelectionStore.bumpQty(1)}
                            aria-label="Increase">+</button>
                  </div>
                </div>
                <div className="ps__qtyrow">
                  <label className="ps__fieldlabel">Unit price</label>
                  {block.allowPriceOverride ? (
                    <input
                      type="number"
                      className="ps__input ps__input--price"
                      value={sel.unitPrice}
                      min={0}
                      onChange={(e) => productSelectionStore.setUnitPrice(e.currentTarget.value)}
                    />
                  ) : (
                    <span className="ps__priceval">{formatCurrency(sel.unitPrice, currency)}</span>
                  )}
                </div>
                <div className="ps__qtyrow ps__qtyrow--total">
                  <label className="ps__fieldlabel">Line total</label>
                  <strong>{formatCurrency(sel.qty * sel.unitPrice, currency)}</strong>
                </div>
              </section>

              {validationBanner && (
                <div className="ps__validmsg" role="alert">
                  <span className="ps__validicon" aria-hidden="true">⚠</span>
                  <span>{validationBanner}</span>
                </div>
              )}

              <div className="ps__actions">
                {sel.editingCartItemId ? (
                  <button type="button" className="ps__btn ps__btn--ghost" onClick={cancelEdit}>
                    Cancel edit
                  </button>
                ) : (
                  <button type="button" className="ps__btn ps__btn--ghost" onClick={() => goTo('style')}>
                    ← Back
                  </button>
                )}
                <button type="button" className="ps__btn ps__btn--primary" onClick={addToOrder}>
                  {sel.editingCartItemId ? 'Update item' : 'Add to order'}
                </button>
              </div>
            </div>

            {hasEmbeddedPreview && (
              <aside className="ps__previewpanel">
                <ProductPreviewRenderer
                  block={{
                    id: 'embedded-preview',
                    type: 'product_preview',
                    attributesSource: 'product_selection_state',
                    layout: 'inline',
                    showAttributeChips: false,
                    showCaption: false,
                    emptyStateTitle: 'Live preview',
                  }}
                  variant={block.previewDomain === 'gallery' ? 'gallery' : 'garment'}
                />
              </aside>
            )}
          </div>
        )
      )}

      {sel.modalOpen && <CatalogueModal block={block} onClose={closeCatalogue} />}
    </section>
  )
}

// ─── PrefField ─────────────────────────────────────────────
// Renders a single preference field per its `control` hint, matching
// the Angular template branches: swatch, pill (with optional image
// pills), radio, toggle, text, default → select.
function PrefField({
  field, value, error, blockId,
}: {
  field: ErpFieldDef
  value: unknown
  error?: string
  blockId: string
}) {
  const control = field.control || 'select'
  const set = (v: unknown) => productSelectionStore.setPref(field.key, v)
  const fieldCls = `ps__field${error ? ' ps__field--invalid' : ''}${control === 'text' ? ' ps__field--full' : ''}`

  return (
    <div className={fieldCls}>
      <label className="ps__fieldlabel">
        {field.label}
        {field.required && <span className="ps__req">*</span>}
      </label>

      {control === 'swatch' && (
        <>
          <div className="ps__swatches">
            {(field.options || []).map((o) => (
              <button
                key={o.value}
                type="button"
                className={`ps__swatch${value === o.value ? ' is-active' : ''}`}
                style={{ background: o.swatch || 'var(--pg-surface-alt)' }}
                title={o.label}
                onClick={() => set(o.value)}
              />
            ))}
          </div>
          <span className="ps__fieldhint">{optionLabel(field, value)}</span>
        </>
      )}

      {control === 'pill' && (
        hasImagePills(field) ? (
          <div className="ps__imgpills">
            {(field.options || []).map((o) => (
              <button
                key={o.value}
                type="button"
                className={`ps__imgpill${value === o.value ? ' is-active' : ''}`}
                onClick={() => set(o.value)}
              >
                {o.imageUrl ? (
                  <span className="ps__imgpill-thumb"
                        style={{ backgroundImage: `url(${o.imageUrl})` }} />
                ) : (
                  <span className="ps__imgpill-thumb ps__imgpill-thumb--blank" />
                )}
                <span className="ps__imgpill-label">{o.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="ps__pills">
            {(field.options || []).map((o) => (
              <button
                key={o.value}
                type="button"
                className={`ps__pill${value === o.value ? ' is-active' : ''}`}
                onClick={() => set(o.value)}
              >{o.label}</button>
            ))}
          </div>
        )
      )}

      {control === 'radio' && (
        <div className="ps__radios">
          {(field.options || []).map((o) => (
            <label key={o.value} className="ps__radio">
              <input
                type="radio"
                name={`${blockId}:${field.key}`}
                value={o.value}
                checked={value === o.value}
                onChange={() => set(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}

      {control === 'toggle' && (
        <label className="ps__toggle">
          <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => set(e.currentTarget.checked)}
          />
          <span>{value ? 'On' : 'Off'}</span>
        </label>
      )}

      {control === 'text' && (
        <input
          type="text"
          className="ps__input"
          value={(value as string) || ''}
          placeholder={field.placeholder || ''}
          onChange={(e) => set(e.currentTarget.value)}
        />
      )}

      {control === 'select' && (
        <select
          className="ps__input"
          value={(value as string) || ''}
          onChange={(e) => set(e.currentTarget.value)}
        >
          <option value="">— Select —</option>
          {(field.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}

      {error && <span className="ps__fielderr">{error}</span>}
    </div>
  )
}

// ─── MeasField ─────────────────────────────────────────────
function MeasField({
  field, value, error, unit,
}: {
  field: ErpFieldDef
  value: number | '' | undefined
  error?: string
  unit: string
}) {
  const cls = `ps__field ps__field--meas${error ? ' ps__field--invalid' : ''}`
  const display = value === undefined || value === '' ? '' : String(value)
  return (
    <div className={cls}>
      <label className="ps__fieldlabel">
        {field.label}
        {field.required && <span className="ps__req">*</span>}
      </label>
      <div className="ps__measinput">
        <input
          type="number"
          className="ps__input"
          min={field.min}
          max={field.max}
          value={display}
          onChange={(e) => productSelectionStore.setMeas(field.key, e.currentTarget.value)}
        />
        <span className="ps__measunit">{field.unit || unit}</span>
      </div>
      {field.min !== undefined && field.max !== undefined && (
        <span className="ps__fieldhint">{field.min}–{field.max}</span>
      )}
      {error && <span className="ps__fielderr">{error}</span>}
    </div>
  )
}

// ─── CatalogueModal (lightweight) ──────────────────────────
// The Angular runtime ships a full 1200-line catalogue modal with
// category sidebar, filter panel, search, drawer-style cart, etc. To
// keep the React preview functional without porting that whole
// surface, this scaffold shows the product grid in a modal and
// delegates the configure flow back to the inline wizard once the
// modal closes — preserving the same data model, ERP calls, and
// shared state.
function CatalogueModal({
  block, onClose,
}: { block: ProductSelectionBlock; onClose: () => void }) {
  const [items, setItems] = useState<ErpProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    setLoading(true)
    listProducts({ page: 1, pageSize: 48, sort: 'popular' })
      .then((res) => { setItems(res.items); setLoading(false) })
      .catch((err) => {
        setErrorMsg(erpErrorMessage(err, 'Failed to load catalogue.'))
        setLoading(false)
      })
  }, [])

  function pick(p: ErpProduct) {
    productSelectionStore.setSelectedProduct(p)
    productSelectionStore.setSelectedStyle(null)
    productSelectionStore.setUnitPrice(p.basePrice)
    onClose()
  }

  const cols = block.catalogueColumns || 4

  return (
    <div className="ecat-overlay" role="dialog" aria-modal="true">
      <div className="ecat-modal">
        <header className="ecat-modal__head">
          <h3 className="ecat-modal__title">{block.catalogueTitle || 'Catalogue'}</h3>
          <button type="button" className="ecat-modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>
        {loading ? <div className="ps__hint">Loading catalogue…</div>
        : errorMsg ? <div className="ps__error">{errorMsg}</div>
        : (
          <div className="ps__grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {items.map((p) => (
              <button key={p.id} type="button" className="ps__card" onClick={() => pick(p)}>
                <div className="ps__cardimg" style={{ backgroundImage: `url(${p.imageUrl})` }} />
                <div className="ps__cardbody">
                  <div className="ps__cardname">{p.name}</div>
                  <div className="ps__cardprice">{formatCurrency(p.basePrice, p.currency)}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
